// Sessões de tempo (`time_entries`) da tela de Sessão (Etapa 12 — spec
// S03). Ponte entre o banco e o módulo puro `src/domain/timer.ts`: aqui
// lemos as linhas reais (sessão aberta + sessões fechadas do projeto,
// eventos de contador) e delegamos toda a MATEMÁTICA de duração/
// implausibilidade para timer.ts — nunca recalculamos nada aqui, nunca
// acumulamos tempo em memória (regra invíolável #5 do projeto).
import { and, eq, inArray, isNull } from 'drizzle-orm';
import type { InferInsertModel } from 'drizzle-orm';
import { timeEntries, counters, counterEvents, projects, type TimeEntry } from '../../db/schema';
import { insertRecord, updateRecord } from '../../db/writes';
import type { AppDatabase } from '../../db/outbox';
import { calculateElapsedSeconds, isSessionImplausible, type ClosedSession } from '../../domain/timer';

/** Formas explícitas (não derivadas genericamente) dos patches de
 * `first_stitch_at`/`last_stitch_at`/`ended_at` — ver o comentário grande em
 * `src/features/session/counters.ts` (`CounterNamePatch`/`renameCounter`)
 * sobre por que essas interfaces são escritas à mão e por que o cast no
 * ponto de chamada a `updateRecord` é necessário (limitação comprovada do
 * TypeScript com `InferInsertModel<TTable>` genérico sobre a união
 * `WritableTable`, não um "as any" às cegas: o typo continua sendo pego na
 * atribuição a estas interfaces, antes do cast). */
interface ProjectFirstStitchPatch {
  firstStitchAt: Date;
}
interface ProjectLastStitchPatch {
  lastStitchAt: Date;
}
interface TimeEntryEndedAtPatch {
  endedAt: Date;
}

/** Linha aberta (`ended_at IS NULL`) de `time_entries` para o projeto, se houver. */
export async function getOpenTimeEntry(db: AppDatabase, projectId: string): Promise<TimeEntry | undefined> {
  return db
    .select()
    .from(timeEntries)
    .where(and(eq(timeEntries.projectId, projectId), isNull(timeEntries.endedAt), isNull(timeEntries.deletedAt)))
    .get();
}

/** Sessão aberta (`ended_at IS NULL`) entre uma lista de projetos, se houver
 * — usada pela lista de Projetos para destacar/fixar no topo o projeto com
 * cronômetro rodando agora, mesmo antes de pausar (quando `last_stitch_at`
 * ainda não foi atualizado). Sem `.innerJoin` (mesma limitação do driver
 * documentada em projectRepository.ts): recebe os IDs já carregados pela
 * tela em vez de juntar com `projects` aqui. Se por algum motivo defensivo
 * houver mais de uma sessão aberta entre os projetos, devolve a mais
 * recente (nunca deveria acontecer pela UI normal). */
export async function getOpenTimeEntryForProjects(db: AppDatabase, projectIds: string[]): Promise<TimeEntry | null> {
  if (projectIds.length === 0) return null;

  const rows = await db
    .select()
    .from(timeEntries)
    .where(and(inArray(timeEntries.projectId, projectIds), isNull(timeEntries.endedAt), isNull(timeEntries.deletedAt)));

  if (rows.length === 0) return null;
  return rows.reduce((latest, row) => (row.startedAt.getTime() > latest.startedAt.getTime() ? row : latest));
}

/** Sessões já fechadas (`ended_at IS NOT NULL`) do projeto, não excluídas. */
export async function listClosedTimeEntries(db: AppDatabase, projectId: string): Promise<TimeEntry[]> {
  const rows = await db
    .select()
    .from(timeEntries)
    .where(and(eq(timeEntries.projectId, projectId), isNull(timeEntries.deletedAt)));
  return rows.filter((row) => row.endedAt !== null);
}

/**
 * Duração total exibível AGORA = timer.ts aplicado sobre o snapshot lido do
 * banco (sessão aberta, se houver, + sessões fechadas). Ponto único que a
 * UI chama nas transições de estado (montar a tela, iniciar, pausar,
 * retomar) — NUNCA a cada tick do relógio (ver SessionScreen: o
 * `setInterval` de 1s só atualiza `now` em memória e recalcula via
 * `calculateElapsedSeconds` local, sem reconsultar o banco a cada segundo).
 */
export async function getElapsedSeconds(db: AppDatabase, projectId: string, now: Date = new Date()): Promise<number> {
  const [open, closed] = await Promise.all([getOpenTimeEntry(db, projectId), listClosedTimeEntries(db, projectId)]);

  const closedSessions: ClosedSession[] = closed
    .filter((entry): entry is TimeEntry & { endedAt: Date } => entry.endedAt !== null)
    .map((entry) => ({ startedAt: entry.startedAt, endedAt: entry.endedAt }));

  return calculateElapsedSeconds({
    openSessionStartedAt: open ? open.startedAt : null,
    closedSessions,
    now,
  });
}

/**
 * Timestamp do toque de contador mais recente entre TODOS os contadores do
 * projeto (primário + extras, mesmo os já excluídos por soft delete — o
 * histórico de eventos continua valendo como "atividade") cujo `at` seja
 * `>= since`. `since` é o `started_at` da sessão aberta: só nos importa
 * atividade DEPOIS que esta sessão específica começou. `null` se nenhum
 * toque aconteceu nesse intervalo.
 */
export async function getLastCounterActivityAt(db: AppDatabase, projectId: string, since: Date): Promise<Date | null> {
  const projectCounters = await db.select({ id: counters.id }).from(counters).where(eq(counters.projectId, projectId));
  if (projectCounters.length === 0) {
    return null;
  }

  const counterIds = projectCounters.map((c) => c.id);
  const events = await db.select().from(counterEvents).where(inArray(counterEvents.counterId, counterIds));

  let latest: Date | null = null;
  for (const event of events) {
    if (event.at.getTime() < since.getTime()) continue;
    if (!latest || event.at.getTime() > latest.getTime()) {
      latest = event.at;
    }
  }
  return latest;
}

export interface ImplausibleSessionCheck {
  timeEntry: TimeEntry;
  implausible: boolean;
}

/**
 * Verifica, para a sessão aberta do projeto (se houver), se ela é
 * "implausível" (spec S03 — ver `isSessionImplausible` em
 * src/domain/timer.ts para a definição exata: inatividade prolongada, não
 * só duração). Devolve `null` quando não há sessão aberta.
 */
export async function checkImplausibleOpenSession(
  db: AppDatabase,
  projectId: string,
  now: Date = new Date()
): Promise<ImplausibleSessionCheck | null> {
  const open = await getOpenTimeEntry(db, projectId);
  if (!open) return null;

  const lastActivityAt = await getLastCounterActivityAt(db, projectId, open.startedAt);
  const implausible = isSessionImplausible({ startedAt: open.startedAt, lastActivityAt, now });

  return { timeEntry: open, implausible };
}

/**
 * Inicia uma sessão de tempo para o projeto. Idempotente: se já existe uma
 * sessão aberta, devolve ela em vez de criar uma segunda (nunca deveria
 * acontecer pela UI normal — o botão de "iniciar" fica escondido quando já
 * há uma sessão aberta — mas defensivo contra corrida/duplo toque).
 *
 * Materializa `projects.first_stitch_at` = `started_at` da PRIMEIRA sessão
 * de tempo já registrada no projeto — nunca muda depois (spec S03).
 */
export async function startTimeEntry(db: AppDatabase, projectId: string): Promise<TimeEntry> {
  const existing = await getOpenTimeEntry(db, projectId);
  if (existing) {
    return existing;
  }

  const entry = await insertRecord(db, timeEntries, { projectId, startedAt: new Date() });

  const project = await db.select().from(projects).where(eq(projects.id, projectId)).get();
  if (project && !project.firstStitchAt) {
    const patch: ProjectFirstStitchPatch = { firstStitchAt: entry.startedAt };
    await updateRecord(db, projects, projectId, patch as Partial<InferInsertModel<typeof projects>>);
  }

  return entry;
}

/**
 * Pausa (fecha) a sessão de tempo: seta `ended_at`. Materializa
 * `projects.last_stitch_at` = este `ended_at` (spec S03: "last_stitch_at =
 * atualizado ao pausar").
 */
export async function pauseTimeEntry(db: AppDatabase, timeEntryId: string, endedAt: Date = new Date()): Promise<TimeEntry> {
  const timeEntryPatch: TimeEntryEndedAtPatch = { endedAt };
  const entry = await updateRecord(db, timeEntries, timeEntryId, timeEntryPatch as Partial<InferInsertModel<typeof timeEntries>>);
  const projectPatch: ProjectLastStitchPatch = { lastStitchAt: endedAt };
  await updateRecord(db, projects, entry.projectId, projectPatch as Partial<InferInsertModel<typeof projects>>);
  return entry;
}

/**
 * Ajusta o fim de uma sessão implausível (diálogo "você continuou
 * trabalhando?" — spec S03). Reaproveita `pauseTimeEntry`: do ponto de
 * vista do banco, ajustar o fim de uma sessão implausível É pausá-la com um
 * `ended_at` escolhido pela usuária em vez de "agora" por padrão.
 */
export async function adjustImplausibleSession(db: AppDatabase, timeEntryId: string, endedAt: Date): Promise<TimeEntry> {
  return pauseTimeEntry(db, timeEntryId, endedAt);
}
